import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Trophy, XIcon, Maximize2 } from 'lucide-react';
import { Tournament, Team, Club, Config, BracketMatch, Match } from '../../types';
import { MatchNode } from '../match/MatchNode';
import { RuleButton } from '../common/RuleButton';

interface SymmetricBracketProps {
  tournament: Tournament;
  config: Config;
  allTeams: Team[];
  clubs: Club[];
  onPlayerClick: (id: string | null) => void;
  isAdmin?: boolean;
  onEditMatch?: (bIdx: number, mIdx: number, match: BracketMatch) => void;
  onDropTeam?: (bIdx: number, mIdx: number, teamId: string, teamName: string, slot: 1 | 2, sourceInfo?: any) => void;
  onRemoveTeam?: (bIdx: number, mIdx: number, slot: 1 | 2) => void;
  onPublishMatch?: (bIdx: number, mIdx: number, match: BracketMatch) => void;
  onResetBrackets?: () => void;
  handleShareCanvas?: (canvas: HTMLCanvasElement, filename: string, action: 'download' | 'whatsapp') => void;
  globalMatches?: Match[];
  actionButtons?: React.ReactNode;
  onEditDateTime?: (bIdx: number, mIdx: number, match: BracketMatch) => void;
  onResetConfirmStatus?: () => void;
}

export function SymmetricBracket({
  tournament,
  config,
  allTeams,
  clubs,
  onPlayerClick,
  isAdmin = false,
  onEditMatch = undefined,
  onDropTeam,
  onRemoveTeam,
  onPublishMatch,
  onResetBrackets,
  onResetConfirmStatus,
  handleShareCanvas,
  globalMatches = [],
  actionButtons,
  onEditDateTime,
}: SymmetricBracketProps) {
  const validBrackets = tournament.brackets || [];
  if (validBrackets.length === 0) return null;

  const finalRound = validBrackets.find((b) => b.matches.length === 1);
  const regularRounds = validBrackets.filter((b) => b.matches.length > 1);

  const [lines, setLines] = useState<any[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<number | null>(null);

  const startAutoScroll = (direction: 'up' | 'down' | 'left' | 'right') => {
    if (scrollIntervalRef.current) return;
    scrollIntervalRef.current = window.setInterval(() => {
      const amount = 15;
      if (direction === 'up') window.scrollBy(0, -amount);
      if (direction === 'down') window.scrollBy(0, amount);
      if (direction === 'left') {
        if (containerRef.current?.parentElement) containerRef.current.parentElement.scrollBy(-amount, 0);
      }
      if (direction === 'right') {
        if (containerRef.current?.parentElement) containerRef.current.parentElement.scrollBy(amount, 0);
      }
    }, 20);
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleGlobalDragOver = (e: React.DragEvent) => {
    if (!isAdmin) return;
    const threshold = 80;
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;

    let found = false;
    if (clientY < threshold) { startAutoScroll('up'); found = true; }
    else if (clientY > innerHeight - threshold) { startAutoScroll('down'); found = true; }
    
    // For horizontal scroll in mobile/small screens
    if (clientX < threshold) { startAutoScroll('left'); found = true; }
    else if (clientX > innerWidth - threshold) { startAutoScroll('right'); found = true; }

    if (!found) stopAutoScroll();
  };

  const handleGlobalDragEnd = () => stopAutoScroll();

  const handleGlobalTouchMove = (e: React.TouchEvent) => {
    if (!isAdmin) return;
    const threshold = 100;
    const { clientX, clientY } = e.touches[0];
    const { innerWidth, innerHeight } = window;

    let found = false;
    if (clientY < threshold) { startAutoScroll('up'); found = true; }
    else if (clientY > innerHeight - threshold) { startAutoScroll('down'); found = true; }
    
    if (clientX < threshold) { startAutoScroll('left'); found = true; }
    else if (clientX > innerWidth - threshold) { startAutoScroll('right'); found = true; }

    if (!found) stopAutoScroll();
  };

  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleCloseFullscreen = () => {
    if (window.history.state?.fullscreen) {
      window.history.back();
    } else {
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    if (!isFullscreen) return;

    const onPopState = () => {
      setIsFullscreen(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseFullscreen();
      }
    };

    window.history.pushState({ fullscreen: true }, '');
    window.addEventListener('popstate', onPopState);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isFullscreen]);

  useEffect(() => {
    const updateLines = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newLines: any[] = [];

      for (let bIdx = 0; bIdx < validBrackets.length - 1; bIdx++) {
        const b = validBrackets[bIdx];
        const nextB = validBrackets[bIdx + 1];
        if (!nextB) continue;

        for (let mIdx = 0; mIdx < b.matches.length; mIdx++) {
          const m = b.matches[mIdx];
          const isLeft = mIdx < b.matches.length / 2;
          const nextMIdx = Math.floor(mIdx / 2);
          
          const fromEl = document.getElementById(`match-${tournament.id}-${bIdx}-${mIdx}`);
          const toEl = document.getElementById(`match-${tournament.id}-${bIdx + 1}-${nextMIdx}`);

          if (fromEl && toEl) {
            const getPos = (el: HTMLElement, container: HTMLElement) => {
              let top = 0;
              let left = 0;
              let curr: HTMLElement | null = el;
              while (curr && curr !== container) {
                top += curr.offsetTop;
                left += curr.offsetLeft;
                curr = curr.offsetParent as HTMLElement;
              }
              return { top, left, width: el.offsetWidth, height: el.offsetHeight };
            };

            const fromR = getPos(fromEl, containerRef.current);
            const toR = getPos(toEl, containerRef.current);

            let startX, startY, endX, endY;
            startY = fromR.top + fromR.height / 2;
            endY = toR.top + toR.height / 2;

            if (isLeft) {
              startX = fromR.left + fromR.width;
              endX = toR.left;
            } else {
              startX = fromR.left;
              endX = toR.left + toR.width;
            }

            newLines.push({ startX, startY, endX, endY, isLeft, hasWinner: !!m.winnerId });
          }
        }
      }
      setLines(newLines);
    };

    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updateLines();
      });
      resizeObserver.observe(containerRef.current);
      // Observe all match nodes for inner size changes
      const nodes = containerRef.current.querySelectorAll('[id^="match-"]');
      nodes.forEach(node => resizeObserver?.observe(node));
    }

    const timer = setTimeout(updateLines, 100);
    const timer2 = setTimeout(updateLines, 1000); // fallback
    window.addEventListener('resize', updateLines);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      window.removeEventListener('resize', updateLines);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [tournament.brackets, isFullscreen]);

  const getBracketTeamInfo = (teamId: string, fallbackName?: string) => {
    if (teamId) {
      const found = allTeams.find((tm) => tm.id === teamId);
      return found
        ? { id: found.id, name: found.name, photoUrl: found.photoUrl }
        : { id: null, name: fallbackName || 'A DEFINIR', photoUrl: null };
    }
    return { id: null, name: fallbackName || 'A DEFINIR', photoUrl: null };
  };

  const content = (
    <div className={isFullscreen ? "fixed inset-0 z-[9999999] bg-[#0a0f18] m-0 p-0 overflow-hidden flex flex-col animate-in zoom-in-95 duration-200" : "mt-12 relative w-full border-t border-white/10 pt-8"}>
      
      {isFullscreen && (
        <button 
          onClick={handleCloseFullscreen}
          className="fixed top-4 right-4 z-[99999999] bg-white/10 hover:bg-white/20 text-white p-3 rounded-full backdrop-blur-md shadow-xl transition-all"
        >
          <XIcon size={24} />
        </button>
      )}

      {isFullscreen && (
        <div className="absolute top-6 left-6 z-[100000] flex items-center gap-2">
          <Trophy className="text-yellow-500 w-6 h-6" />
          <span className="text-white font-black uppercase tracking-widest text-sm">Play-Offs</span>
        </div>
      )}

      {!isFullscreen && (
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 px-3 md:px-6 py-2.5 bg-gradient-to-r from-white/[0.05] to-transparent rounded-xl border border-white/5 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full theme-accent-bg flex items-center justify-center shadow-[0_0_15px_rgba(var(--accent-rgb),0.2)] shrink-0">
              <Trophy className="text-black w-4 h-4 md:w-5 md:h-5" />
            </div>
            <div className="flex flex-col">
              <h4 className="text-lg md:text-xl font-black uppercase tracking-[0.15em] theme-font-secondary leading-none">
                {isAdmin ? 'Fase de eliminación' : 'Play-Offs'}
              </h4>
              <div className="h-0.5 w-8 theme-accent-bg mt-1.5 rounded-full opacity-50"></div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {actionButtons && (
              <div className="flex items-center gap-2">
                {actionButtons}
              </div>
            )}
          </div>
        </div>
      )}

      <div 
        className={`w-full overflow-x-auto custom-scrollbar relative z-10 ${isFullscreen ? 'flex-1 h-full flex flex-col justify-center items-center md:py-20 md:px-10 max-md:w-[100vh] max-md:h-[100vw] max-md:absolute max-md:top-1/2 max-md:left-1/2 max-md:-translate-x-1/2 max-md:-translate-y-1/2 max-md:-rotate-90' : 'pb-8'}`}
        onDragOver={handleGlobalDragOver}
        onDragEnd={handleGlobalDragEnd}
        onDrop={handleGlobalDragEnd}
      >
        <div ref={containerRef} className="w-max min-w-full mx-auto px-2 md:px-4 flex justify-center relative">
          <svg className="absolute inset-0 pointer-events-none overflow-visible" style={{ width: '100%', height: '100%', zIndex: 0 }}>
            {lines.map((l, i) => {
              const dx = l.endX - l.startX;
              const control = Math.max(Math.abs(dx) / 2, 10);
              const d = l.isLeft 
                ? `M ${l.startX} ${l.startY} C ${l.startX + control} ${l.startY}, ${l.endX - control} ${l.endY}, ${l.endX} ${l.endY}`
                : `M ${l.startX} ${l.startY} C ${l.startX - control} ${l.startY}, ${l.endX + control} ${l.endY}, ${l.endX} ${l.endY}`;

              return (
                <path 
                  key={i} 
                  d={d} 
                  stroke={l.hasWinner ? (config.primaryColor || '#eab308') : 'rgba(255,255,255,0.08)'} 
                  strokeWidth={l.hasWinner ? "2" : "1.5"} 
                  fill="none" 
                  className={l.hasWinner ? "drop-shadow-[0_0_8px_rgba(var(--glow-rgb),0.5)]" : ""}
                />
              );
            })}
          </svg>

          <div className="flex items-stretch justify-center gap-2 sm:gap-4 md:gap-8 relative z-10 w-full">
            <div className="flex flex-1 justify-end gap-2 sm:gap-4 md:gap-8 pr-2 sm:pr-4 md:pr-8">
              {regularRounds.map((b, bIdx) => {
                const originalBIdx = validBrackets.findIndex((br) => br.round === b.round);
                return (
                  <div key={`L-${originalBIdx}`} className="flex flex-col justify-around gap-2 md:gap-4 w-[160px] sm:w-[180px] md:w-[200px] shrink-0">
                    {b.matches.slice(0, Math.ceil(b.matches.length / 2)).map((m, mIdx) => (
                      <MatchNode
                        id={`match-${tournament.id}-${originalBIdx}-${mIdx}`}
                        key={`L-m-${originalBIdx}-${mIdx}`}
                        match={m}
                        roundName={b.round}
                        getTeamInfo={getBracketTeamInfo}
                        isAdmin={isAdmin}
                        onEdit={() => onEditMatch && onEditMatch(originalBIdx, mIdx, m)}
                        onDropTeam={(teamId, teamName, slot, source) => onDropTeam?.(originalBIdx, mIdx, teamId, teamName, slot, source)}
                        onDropGlobal={onDropTeam}
                        onRemoveTeam={(slot) => onRemoveTeam?.(originalBIdx, mIdx, slot)}
                        onPublishMatch={() => onPublishMatch?.(originalBIdx, mIdx, m)}
                        config={config}
                        bIdx={originalBIdx}
                        mIdx={mIdx}
                        globalMatches={globalMatches}
                        isExpressMode={tournament.managementMode === 'express'}
                        onEditDateTime={() => onEditDateTime?.(originalBIdx, mIdx, m)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>

            {finalRound && (
              <div className="flex flex-col justify-center px-2 sm:px-4 shrink-0 relative z-10 w-[160px] sm:w-[180px] md:w-[200px]">

                <div className="text-center mb-3">
                  <span className="theme-accent-bg font-black px-4 py-1.5 rounded-full text-[9px] md:text-[10px] uppercase tracking-[0.2em] shadow-[0_0_15px_rgba(var(--accent-rgb),0.3)] text-black">
                    {finalRound.round}
                  </span>
                </div>
                <MatchNode
                  id={`match-${tournament.id}-${validBrackets.findIndex((br) => br.round === finalRound.round)}-0`}
                  match={finalRound.matches[0]}
                  roundName=""
                  getTeamInfo={getBracketTeamInfo}
                  isFinal={true}
                  isAdmin={isAdmin}
                  onEdit={() =>
                    onEditMatch &&
                    onEditMatch(
                      validBrackets.findIndex((br) => br.round === finalRound.round),
                      0,
                      finalRound.matches[0]
                    )
                  }
                  onDropTeam={(teamId, teamName, slot, source) => onDropTeam?.(validBrackets.findIndex((br) => br.round === finalRound.round), 0, teamId, teamName, slot, source)}
                  onDropGlobal={onDropTeam}
                  onRemoveTeam={(slot) => onRemoveTeam?.(validBrackets.findIndex((br) => br.round === finalRound.round), 0, slot)}
                  onPublishMatch={() => onPublishMatch?.(validBrackets.findIndex((br) => br.round === finalRound.round), 0, finalRound.matches[0])}
                  config={config}
                  bIdx={validBrackets.findIndex((br) => br.round === finalRound.round)}
                  mIdx={0}
                  globalMatches={globalMatches}
                  isExpressMode={tournament.managementMode === 'express'}
                  onEditDateTime={() => onEditDateTime?.(validBrackets.findIndex((br) => br.round === finalRound.round), 0, finalRound.matches[0])}
                />

                <div className="mt-12 opacity-75 scale-[0.9] grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-300">
                  <div className="text-center mb-1.5">
                    <span className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.2em] opacity-40">
                      (opcional)
                    </span>
                  </div>
                  <MatchNode
                    id={`match-${tournament.id}-999-0`}
                    match={tournament.thirdPlaceMatch || { team1Id: '', team2Id: '', s1: '', s2: '', s3: '', score: '', winnerId: '' }}
                    roundName="3er Puesto"
                    getTeamInfo={getBracketTeamInfo}
                    isAdmin={isAdmin}
                    onEdit={() => onEditMatch && onEditMatch(999, 0, tournament.thirdPlaceMatch || { team1Id: '', team2Id: '', s1: '', s2: '', s3: '', score: '', winnerId: '' })}
                    onDropTeam={(teamId, teamName, slot, source) => onDropTeam?.(999, 0, teamId, teamName, slot, source)}
                    onDropGlobal={onDropTeam}
                    onRemoveTeam={(slot) => onRemoveTeam?.(999, 0, slot)}
                    onPublishMatch={() => onPublishMatch?.(999, 0, tournament.thirdPlaceMatch || { team1Id: '', team2Id: '', s1: '', s2: '', s3: '', score: '', winnerId: '' })}
                    config={config}
                    bIdx={999}
                    mIdx={0}
                    globalMatches={globalMatches}
                    isExpressMode={tournament.managementMode === 'express'}
                    onEditDateTime={() => onEditDateTime?.(999, 0, tournament.thirdPlaceMatch || { team1Id: '', team2Id: '', s1: '', s2: '', s3: '', score: '', winnerId: '' })}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-1 justify-start gap-2 sm:gap-4 md:gap-8 pl-2 sm:pl-4 md:pl-8">
              {[...regularRounds].reverse().map((b, revIdx) => {
                const originalBIdx = validBrackets.findIndex((br) => br.round === b.round);
                const halfLength = Math.ceil(b.matches.length / 2);
                return (
                  <div key={`R-${originalBIdx}`} className="flex flex-col justify-around gap-2 md:gap-4 w-[160px] sm:w-[180px] md:w-[200px] shrink-0">
                    {b.matches.slice(halfLength).map((m, relativeMIdx) => {
                      const actualMIdx = halfLength + relativeMIdx;
                      return (
                        <MatchNode
                          id={`match-${tournament.id}-${originalBIdx}-${actualMIdx}`}
                          key={`R-m-${originalBIdx}-${actualMIdx}`}
                          match={m}
                          roundName={b.round}
                          getTeamInfo={getBracketTeamInfo}
                          isAdmin={isAdmin}
                          onEdit={() => onEditMatch && onEditMatch(originalBIdx, actualMIdx, m)}
                          onDropTeam={(teamId, teamName, slot, source) => onDropTeam?.(originalBIdx, actualMIdx, teamId, teamName, slot, source)}
                          onDropGlobal={onDropTeam}
                          onRemoveTeam={(slot) => onRemoveTeam?.(originalBIdx, actualMIdx, slot)}
                          onPublishMatch={() => onPublishMatch?.(originalBIdx, actualMIdx, m)}
                          config={config}
                          bIdx={originalBIdx}
                          mIdx={actualMIdx}
                          globalMatches={globalMatches}
                          isExpressMode={tournament.managementMode === 'express'}
                          onEditDateTime={() => onEditDateTime?.(originalBIdx, actualMIdx, m)}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {!isFullscreen && (
        <div className="flex justify-center mt-2 mb-8 relative z-20">
          <button
            onClick={() => setIsFullscreen(true)}
            className="flex items-center gap-2.5 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg border border-white/10 transition-all active:scale-95"
          >
            <Maximize2 size={16} className="text-yellow-500" />
            <span>Ver pantalla completa</span>
          </button>
        </div>
      )}
    </div>
  );

  return isFullscreen ? createPortal(content, document.body) : content;
}
